import { useTranslation } from "react-i18next";
import { useAuth } from "../../../Auth/AuthProvider";
import { usePaginatedQuery } from "../../../Hooks/usePaginatedQuery";
import { useState, useEffect } from "react";
import DataTable from "../../../Components/DataTable/DataTable";
import { GetVehicleCondition } from "../../../Components/Tables/Vehicles/GetVehicleCondition";
import { convertToPST } from "../../../Helper/FormatDatetimes";
import { getDuration } from "../../../Interceptor/csrftoken";
import DebouncedInput from "../../../Components/Inputs/DebouncedInput";
import { TableNavigation } from "../../../Components/Tables/Vehicles/TableNavigation";
import { buildURLWithParams } from "../../../Components/Utility/URLUtils";
import { NewRowClickModal } from "../../../Components/Modals/RowClickModal";
import { CheckInIcon } from "../../../Media/Icons";
import { calculateHoursDifference } from "../../../Components/Utility/CalculateHoursDifference";
import { EditTruckRow } from "../EditTruckRow";
import TableImgModal from "../../../Components/Modals/TableImgModal";
import EditTruckCondition from "../../../Components/Tables/Vehicles/EditTruckCondition";
import { useApiMutation } from "../../../Hooks/useApiMutation";
import instance from "../../../Interceptor/api_instance";
import { Spinner } from "../../../Components/LoadingAnimations/LoadingAnimations";

const hasGroup = (names, user) =>
  user?.groups?.some((g) =>
    (Array.isArray(names) ? names : [names]).includes(g.name)
  );

const truckColumns = (t, user) => {
  const isPrivileged = hasGroup(["Admin", "Dispatch", "Safety"], user);

  return [
    ...(isPrivileged
      ? [
          {
            key: "location",
            header: t("yard-tables.table-columns.location"),
            getValue: (row) => row.location.loc_name,
          },
        ]
      : []),
    {
      key: "carrier",
      header: t("yard-tables.table-columns.carrier"),
      getValue: (row) => row.truck.carrier_name,
    },
    {
      key: "truck_number",
      header: t("yard-tables.table-columns.truck"),
      getValue: (row) => row.truck.truck_number,
    },
    {
      key: "driver",
      header: t("yard-tables.table-columns.driver"),
      getValue: (row) => row.visitor.full_name,
    },
    {
      key: "guard",
      header: t("yard-tables.table-columns.guard"),
      getValue: (row) =>
        row.employee ? row.employee.full_name : row.user.full_name,
    },
    {
      key: "condition",
      header: t("yard-tables.table-columns.condition"),
      render: (value, row) => (
        <GetVehicleCondition condition={row.truck.truck_condition} />
      ),
    },
    {
      key: "truck_condition_notes",
      header: t("forms.type.vehicle.input-headers.notes"),
      render: (value, row) =>
        row.truck.truck_condition_notes ? (
          <span className="warning">{t("statuses.review")}</span>
        ) : (
          ""
        ),
    },
    {
      key: "arrivalTime",
      header: t("yard-tables.table-columns.arrival-time"),
      getValue: (row) => convertToPST(row.check_in_dt),
    },
    {
      key: "duration",
      header: t("yard-tables.table-columns.duration"),
      render: (value, row) => {
        const duration = calculateHoursDifference(row.check_in_dt);
        return (
          <span style={{ color: duration >= 34 ? "#fb6061" : "" }}>
            {getDuration(row.check_in_dt)}
          </span>
        );
      },
    },
  ];
};

const DEFAULT_URL = "assets/trucks/";
const TrucksInventory = () => {
  const { t } = useTranslation();
  const { user, yardsData } = useAuth();

  const { state, dispatch, selectRow, updateRow, setEdit } =
    usePaginatedQuery(DEFAULT_URL);

  const [filters, setFilters] = useState({
    location: "",
    carrier: "",
    truck_number: "",
  });

  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const columns = truckColumns(t, user);

  const handleFilter = (name, value) => {
    const newFilters = {
      ...filters,
      [name]: value,
    };
    setFilters(newFilters);
    dispatch({
      type: "SET_CURRENT_PAGE",
      page: buildURLWithParams(DEFAULT_URL, newFilters),
      pageNum: 1,
    });
  };

  const { mutate, isLoading, error } = useApiMutation(async () => {
    const response = await instance.post("/files/export-yard-inventory/", {
      export_type: "trucks",
    });
    if (response.status === 201) {
      setSuccessMessage(t("forms.responses.success"));
      return response.data;
    }
  });

  return (
    <>
      {state.selectedRow &&
        (hasGroup(["Admin", "Safety", "Night Dispatch", "Mechanic"], user) ? (
          <NewRowClickModal
            row={state.selectedRow}
            viewImages={() => dispatch({ type: "OPEN_DOCS" })}
            editRow={() => dispatch({ type: "EDIT_ROW" })}
            close={() => selectRow(null)}
          />
        ) : (
          <TableImgModal
            info={state.selectedRow.truck.truck_number}
            close={() => selectRow(null)}
            trailer={state.selectedRow}
            entity_type={"truck"}
          />
        ))}
      {state.docsModal && state.selectedRow && (
        <TableImgModal
          info={state.selectedRow.truck.truck_number}
          close={() => dispatch({ type: "CLOSE_DOCS" })}
          trailer={state.selectedRow}
          entity_type={"truck"}
        />
      )}
      {state.editRow &&
        state.selectedRow &&
        (hasGroup(["Mechanic"], user) ? (
          <EditTruckCondition
            row={state.selectedRow}
            close={() => dispatch({ type: "CLOSE_EDIT_ROW" })}
          />
        ) : (
          <EditTruckRow
            row={state.selectedRow}
            close={() => dispatch({ type: "CLOSE_EDIT_ROW" })}
            onSuccess={(data, key) => {
              updateRow(data, key);
              setSuccessMessage(
                t("forms.responses.success") || "Updated successfully"
              );
              setEdit(false);
              selectRow(null);
            }}
          />
        ))}

      <div className="search-filters-cont">
        <div className="search-filters">
          {/* Yards filter */}
          {hasGroup(["Admin", "Safety", "Dispatch"], user) && (
            <select
              className="text-input-sm"
              value={filters.location}
              onChange={(e) => handleFilter("location", e.target.value)}
              name="location"
            >
              <option value="">
                {t("yard-tables.table-columns.location")}
              </option>
              {yardsData.map((yard) => (
                <option key={`yard-${yard.id}`} value={yard.id}>
                  {yard.loc_name}
                </option>
              ))}
            </select>
          )}

          {/* Carrier filter */}
          <DebouncedInput
            state={filters.carrier}
            setState={handleFilter}
            label={"carrier"}
            name={"carrier"}
          />

          <DebouncedInput
            state={filters.truck_number}
            setState={handleFilter}
            label={"truck"}
            name={"truck_number"}
          />
        </div>
      </div>
      {isLoading ? (
        <Spinner />
      ) : successMessage ? null : (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <button className="change-pwd-btn" onClick={mutate}>
            {t("buttons.export")}
          </button>
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "12px",
          position: "relative",
        }}
      >
        {successMessage && (
          <div style={{ position: "absolute", left: 0 }}>
            <span>
              <CheckInIcon color={"green"} /> {successMessage}
            </span>
          </div>
        )}
        {error && (
          <div style={{ position: "absolute", left: 0, color: "red" }}>
            <span>
              {t("forms.responses.failure")} {error.message}
            </span>
          </div>
        )}
        <TableNavigation state={state} dispatch={dispatch} />
      </div>

      <DataTable
        columns={columns}
        rows={state.data}
        isLoading={state.isLoading}
        onRowClick={(row) => selectRow(row)}
      />
    </>
  );
};

export default TrucksInventory;
